/**
 * @typedef {Object} SelectedDateLineTrack
 * @property {SelectedDateLineTrackValue} value
 * @property {String} text - display text for the selectedDateLineTrack
 */
/**
 * @typedef {Object} SelectedDateLineTrackValue
 * @property {Number} line - selected line
 * @property {Number} track - selected track (=kurs)
 * @property {String} day - date as ISO string
 */

import { performInMemorySearch } from '@/components/search/searchStore';
import { SYNC_STATES } from '@/store/sync/sync';
import dataUtils from '@/common/dataUtils';
import dateUtils from '@/common/dateUtils';
import favoritesService from '@/services/favoritesService';
import storeService from '@/services/storeService';
import readService from '@/services/readService';
import acknowledgeService from '@/services/acknowledgeService';
import { cacheAllModuleFiles } from '@/store/sync/cachingUtils';
import fahrdienst452Api from './fahrdienst452Api';
import moduleId from './moduleId';
import moduleIndex from './index';
import MODULE_ID from './moduleId';
import IN_MEMORY_DB from './storeNonReactive';
import storeNonReactive from './storeNonReactive';

const USER_STATE_TYPE = 'fahrdienst452UserState';
const ROOT = { root: true };
const USE_PERLSCHNUR_JSON = true;

const state = {
  bemerkungById: {},
  bemerkungIdBy_fahrtId_startOrtIdFk: {},
  bemerkungIdBy_fahrtId_endOrtIdFk: {},

  dayLineKursList: [],
  fahrdienst452File: [],
  fahrdienst452FileById: {},
  fahrdienst452FilesByType_Kurs: {},
  fahrdienst452FileTypes: [],
  fahrdienst452UserState: [],
  fahrdienstLabels: [],

  unreadCount: null,

  // View State
  displayedDocument: null,
  displayMode: 'tour', //or 'document'

  selectedDateLineTrack: null,
  tracksInSelection: [],
  isLoaded: false, // true indicates, data has been loaded (at least one sync process)
  autoScrollToCurrentTime: true,
};

const getters = {
  /** -------------------------------------------- ----------------------------------------------------------------------
   * Module Implementation for unread badge counts by instance.
   * IMPLEMENT this getter to enable unread counter badges
   * -------------------------------------------------------------------------------------------------------------------
   */
  getUnreadCountByModuleInstance: (state) => (instance) => {
    // module does not support instance
    return state.unreadCount;
  },

  getIsLoaded: (state) => state.isLoaded,
};
const mutations = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for UPDATING Module counters
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  countModuleItems(state) {
    let selectedDateTime = new Date().getTime();
    let unreadDocs = state.fahrdienst452File
      // count only documents that are visible at the current date. This is a compromise because the number of unread documents might
      // change from day to day
      .filter((f) => {
        let displayFrom = Date.parse(f.anzeige_von);
        let displayTo = Date.parse(f.anzeige_bis);
        let beginOk = isNaN(displayFrom) ? true : displayFrom <= selectedDateTime;
        let endOk = isNaN(displayTo) ? true : displayTo > selectedDateTime;
        return beginOk && endOk;
      })
      .filter((f) => {
        return (readService.isUnread(f) && f.isRelevant) || acknowledgeService.isToAcknowledge(f);
      });
    state.unreadCount = unreadDocs.length;
  },
  setData(state, { hash, data: serverData }) {
    state.hash = hash;
    let timetableByTourHash = dataUtils.buildIdMap(serverData.timetable, 'tourHash');
    serverData.timetableByTourHash = timetableByTourHash;
    storeService.saveDataToInMemoryStore(moduleId, state, IN_MEMORY_DB, serverData); // reactivity not working!!!!
    state.fahrdienst452File = serverData.fahrdienst452File;
    state.dayLineKursList = serverData.dayLineKursList;
    state.fahrdienst452FileById = serverData.fahrdienst452FileById;
    state.fahrdienst452FilesByType_Kurs = serverData.fahrdienst452FilesByType_Kurs;
    state.fahrdienst452FileTypes = serverData.fahrdienst452FileTypes;
    state.fahrdienst452UserState = serverData.fahrdienst452UserState;
    state.fahrdienstLabels = serverData.fahrdienstLabels;

    state.isLoaded = true;
  },
  resetView: (state) => {
    state.displayMode = null;
    state.displayedDocument = null;
    state.selectedDateLineTrack = null;
  },
  displayDocument: (state) => {
    if (state.displayedDocument) {
      state.displayMode = 'document';
    }
  },
  displayTour: (state) => {
    state.displayMode = 'tour';
  },

  setDisplayMode: (state, displayMode) => {
    state.displayMode = displayMode;
  },
  setDisplayedDocument: (state, displayedDocument) => {
    console.debug('setDisplayedDocument', displayedDocument.id);
    state.displayedDocument = displayedDocument;
    state.tracksInSelection = calculateSelectedTracks(state);
  },
  setAutoScrollToCurrentTime: (state, value) => {
    state.autoScrollToCurrentTime = value;
  },
};

const TIME_PREPROCESSING = 'fahrdienst452 pre-processing:::';
const TIME_SET_DATA = 'fahrdienst452 commit(setData):::';
const actions = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for IN-MEMORY SEARCH
   * IMPLEMENT this getter to enable module in-memory search
   * Call the action in search/searchStore (search for "Module in-memory searches" there)
   * Search all documents containing all of the space separated search words in state.filterCriteria
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  searchModuleInMemoryContainingSearchTerm({ state }, { filterCriteria, options }) {
    return performInMemorySearch({
      moduleId,
      items: state.fahrdienst452File,
      titleSearchAttr: 'title',
      contentSearchAttr: null,
      searchCriteriaArray: filterCriteria,
      options,
    });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Updates the browsers cache storage oevpad-api/modules/{moduleId} with the exact same data structure as received by the module sync api call!
   * Reason: This mutation MUST be called whenever data has changed, e.g. user_state data. It assures that the application cache is up-to-date
   *         when the user synchronized/updates in offline mode.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async updateModuleApplicationCache({ state }) {
    console.debug('skip fahrdienst452/updateModuleApplicationCache');
    return;
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeFiles({ commit, dispatch, state }) {
    await cacheAllModuleFiles({ files: state.fahrdienst452File, moduleIndex, commit, dispatch });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeData({ commit, state, dispatch, rootState, rootGetters }) {
    let loadDataStartTime = Date.now(),
      response;

    try {
      response = await fahrdienst452Api.getData();
    } catch (err) {
      console.error('Failed to fetch fahrdienst452 module data', err);
      return;
    }

    try {
      const hash = response.headers['data-hash'];
      if (hash) localStorage.setItem(`${moduleId}/hash`, JSON.stringify(hash));

      const hasChanges = response.headers['has-change'];
      console.debug(`Fahrdienst452 load data took ${Date.now() - loadDataStartTime} millis.`);

      console.debug('hash=', hash, 'hasChanges', hasChanges);
      if (hasChanges === 'false') {
        console.debug('fahrdienst data not changed');

        return;
      }
      let moduleConfig = rootGetters['configs/getInstanceModuleConfig']({ moduleType: moduleIndex.moduleType });
      const moduleTitle = moduleConfig.title;

      console.time(TIME_PREPROCESSING);
      let data = response.data;

      let preprocessed = preprocessFahrdienst452Data({ moduleConfig, moduleTitle, ...response.data });
      preprocessed.umlaufInfoByTourHash = data.umlaufInfoByTourHash;
      if (!USE_PERLSCHNUR_JSON) {
        preprocessed.fahrzeitBy_startOrtShortName_endOrtShortName = data.fahrzeitBy_startOrtShortName_endOrtShortName;
        preprocessed.haltezeitBy_fahrtId_ortShortName = data.haltezeitBy_fahrtId_ortShortName;
        preprocessed.fahrzeitBy_startOrtIdFk_endOrtIdFk = data.fahrzeitBy_startOrtIdFk_endOrtIdFk;
        preprocessed.haltezeitBy_fahrtId_ortId = data.haltezeitBy_fahrtId_ortId;
        preprocessed.fahrzeitBy_adapter_fgrNr_startOrtIdFk_endOrtIdFk = data.fahrzeitBy_adapter_fgrNr_startOrtIdFk_endOrtIdFk;
        preprocessed.verlaufByAdapterId = data.verlaufByAdapterId;
      } else {
        preprocessed.timetable = data.perlschnurData;
        if (rootState.lifecycle.isPwaMode) {
          //console.debug('downloadAndCachePerlschnurData', storeNonReactive.perlschnurJsonByTourHash);
          await downloadAndCachePerlschnurData(preprocessed.timetable, storeNonReactive.perlschnurJsonByTourHash);
        }
      }
      // add localStorage read state for mandatoryToRead documents
      favoritesService.addFavoriteStatesFromLocalStorage({
        moduleId: 'fahrdienst452',
        items: preprocessed.fahrdienst452File,
      });
      console.time(TIME_SET_DATA);
      let { fahrdienst452FileById } = preprocessed;

      dataUtils.applyLocalStorageUserState(fahrdienst452FileById, USER_STATE_TYPE, 'fileIdFk');
      commit('setData', { hash, data: preprocessed });

      commit('countModuleItems');

      console.timeEnd(TIME_SET_DATA);
      console.timeEnd(TIME_PREPROCESSING);
    } catch (err) {
      console.error('Failed to sync fahrdienst452 module', err);
      commit('sync/setSyncState', { moduleId, syncState: SYNC_STATES.FAILURE, msg: err }, ROOT);
      throw err;
    }
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Dispatched in store/sync !!!
   * Gets dispatched after all sync processes have been completed.
   * This is the place to implement any post sync processes.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async onSyncComplete({ commit, dispatch, rootState, rootGetters }) {
    // Implement this method if necessary
  },

  /**
   * Delegate to the sendbox action and reevaluate store
   * @param {*} state
   * @param {*} param1
   */
  async performDocumentAction({ state, commit, dispatch }, { document, actionType, immediate }) {
    console.debug('performDocumentAction actionType', actionType, document);
    await dispatch(`sendbox/performAction`, { item: document, userStateType: USER_STATE_TYPE, actionType, moduleId: MODULE_ID, immediate }, { root: true });

    commit('countItems');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

// functions ---------------------------------------------------------------

function calculateSelectedTracks(state) {
  if (!state.isLoaded) return [];
  console.debug('calculateSelectedTracks');
  if (state.selectedDateLineTrack && state.selectedDateLineTrack.value) {
    let { line, track, day: dateIsoString } = state.selectedDateLineTrack.value; // rename day to dateIsoString
    let allTracksOfAdapterUmlaufAndDate;
    if (USE_PERLSCHNUR_JSON) {
      return [{ kurs: track }];
    }
    if (dateIsoString && (line || track)) {
      let tracks = IN_MEMORY_DB.fahrt.filter((f) => f.day === dateIsoString && track === f.kurs);
      // if (!tracks) {
      //   tracks = state.fahrtBy_day_linie[`${dateIsoString}_${line}`];
      // }
      if (tracks && tracks.length > 0) {
        let adapter_umlauf = tracks[0].adapter_umlauf;
        allTracksOfAdapterUmlaufAndDate = IN_MEMORY_DB.fahrt_lists_by_day_adapterUmlauf[`${dateIsoString}_${adapter_umlauf}`];
      }
      let tracksCollection = [];
      if (allTracksOfAdapterUmlaufAndDate) {
        tracksCollection = allTracksOfAdapterUmlaufAndDate.sort((f1, f2) => f1.fromMinutesAfterMidnight - f2.fromMinutesAfterMidnight);
      }
      return tracksCollection;
    }
  }
  return [];
}

function preprocessFahrdienst452Data(data) {
  const PREPROCESSING_TIMER = 'preprocessFahrdienst452Data';

  let {
    bemerkungById,
    bemerkungIdBy_fahrtId_startOrtIdFk,
    bemerkungIdBy_fahrtId_endOrtIdFk,
    fahrt,
    verlaufByAdapterLinieId,
    ortById,

    haltezeit,
    // haltezeitBy_fahrtId_ortId,
    haltezeitenFahrtgruppeBy_adapterId_ortId,
    fahrtAnschlussBy_fahrtId_ortId,
    dayLineKursList,
    fahrdienst452File,
    fahrdienst452FileTypes,
    fahrdienst452UserState,
    fahrdienstFileLabels,
    fahrdienstLabels,
    fahrdienst452Lines,

    // fahrtBy_day_kurs,

    moduleTitle,
    moduleConfig,
  } = data;

  console.time(PREPROCESSING_TIMER);
  const fahrdienst452FilesByType_Kurs = {};
  const fahrdienst452FileById = {};
  const userStatesByFileId = dataUtils.buildIdMap(fahrdienst452UserState, 'fileIdFk');
  const fahrdienst452LinesByKuerzel = dataUtils.buildIdMap(fahrdienst452Lines, 'kuerzel');
  const fahrdienst452LinesByLine = dataUtils.buildIdMap(fahrdienst452Lines, 'linie');
  console.time('fahrdienst452File iteration');
  fahrdienst452File.forEach((f) => {
    fahrdienst452FileById[f.id] = f;
    let arrayOfKurs = f.kurs ? f.kurs.split(',') : [];
    arrayOfKurs.forEach((kurs) => {
      dataUtils.push(fahrdienst452FilesByType_Kurs, `${f.typeIdFk}_${kurs}`, f);
    });

    //enhance item with information used for example when searching
    if (f.filename) {
      f._checked = false;
      //f._displayLocation = `${documentTransl} ${f.linie !== null ? lineTransl + ' ' + f.linie : ''}${f.linie ? ' ' : ''}${f.kurs ? trackTransl + ' ' + f.kurs : ''}`;
      f._moduleTitle = moduleTitle;
      f._moduleId = 'fahrdienst452';
      f._path = `/fahrdienst452/document/${f.id}`;
    } else {
      console.debug('fahrdienst452 defines no filename for file', f.id, f.title);
    }

    // Create a userState object in case it does not exist
    // IMPORTANT: Be sure to set all properties which can be changed and should be watched!!!!!
    f._userState = userStatesByFileId[f.id]
      ? userStatesByFileId[f.id]
      : {
          dateread: null,
          favorite: 0,
          dateconfirmed: null,
          fileIdFk: f.id,
        };
    f._userState._module = 'fahrdienst452';
    f._userState._type = 'fahrdienst452UserState';
    favoritesService.assertFavoriteIsSet(f);
  });

  // attach labels array
  fahrdienstFileLabels.forEach((fileLabel) => {
    let file = fahrdienst452FileById[fileLabel.fileIdFk];
    if (file) {
      if (!file._labelIds) file._labelIds = [];
      file._labelIds.push(fileLabel.labelIdFk);
    }
  });

  console.timeEnd('fahrdienst452File iteration');
  let fahrtBy_day_by_kurs, fahrtBy_day_by_adapterUmlauf, fahrt_lists_by_day_adapterUmlauf;

  if (!USE_PERLSCHNUR_JSON) {
    fahrtBy_day_by_kurs = dataUtils.buildTwoLevelObjectWithArrayLeaves(
      fahrt,
      (i) => dateUtils.reformatDateIsoStringAsYYYYMMDD(i.day),
      (i) => i.kurs
    );

    console.time('fahrtBy_day_by_adapterUmlauf');
    fahrtBy_day_by_adapterUmlauf = dataUtils.buildTwoLevelObjectWithArrayLeaves(
      fahrt,
      (i) => dateUtils.reformatDateIsoStringAsYYYYMMDD(i.day),
      (i) => i.adapter_umlauf
    );
    console.timeEnd('fahrtBy_day_by_adapterUmlauf');

    fahrtBy_day_by_kurs = dataUtils.buildTwoLevelObjectWithArrayLeaves(
      fahrt,
      (i) => dateUtils.reformatDateIsoStringAsYYYYMMDD(i.day),
      (i) => i.kurs
    );

    fahrt_lists_by_day_adapterUmlauf = dataUtils.buildCombinedOneToManyMap(fahrt, (i) => `${i.day}_${i.adapter_umlauf}`);
  }

  console.timeEnd(PREPROCESSING_TIMER);
  return {
    bemerkungById,
    bemerkungIdBy_fahrtId_startOrtIdFk,
    bemerkungIdBy_fahrtId_endOrtIdFk,
    fahrt,
    fahrtBy_day_by_kurs,
    fahrt_lists_by_day_adapterUmlauf,
    fahrtBy_day_by_adapterUmlauf, // will replace fahrt_lists_by_day_adapterUmlauf
    verlaufByAdapterLinieId,
    ortById,
    haltezeitenFahrtgruppeBy_adapterId_ortId,
    fahrtAnschlussBy_fahrtId_ortId,
    dayLineKursList,
    fahrdienst452File,
    fahrdienst452FileById,
    fahrdienst452FileTypes,
    fahrdienst452FilesByType_Kurs,
    fahrdienstLabels,
    fahrdienst452LinesByKuerzel,
    fahrdienst452LinesByLine,
  };
}

/**
 * Delete cache entries in oevpad-api because this is the wrong location
 */
async function deleteDeprecatedPerlschnurCacheEntries() {
  // delete cache entries in oevpad-api because this is the wrong location
  let oevpadApiCache = await caches.open('oevpad-api');
  let keys = await oevpadApiCache.keys();
  let perlschnurResourceUrls = keys.filter((k) => k.url.includes('api/modules/fahrdienst452/perlschnur')).map((k) => k.url);
  for (let i = 0; i < perlschnurResourceUrls.length; i++) {
    await oevpadApiCache.delete(perlschnurResourceUrls[i]);
  }
}

/**
 * Download and cache perlschnur json (from fahrdienst452/json folder in upload)
 * @param timetable {Array} - the timetable array from the fahrdienst452_timetable table
 * @param perlschnurJsonByTourHash {Object} - the existing perlschnur map, mapped by tour hash
 * @returns {Promise<void>}
 */
async function downloadAndCachePerlschnurData(timetable, perlschnurJsonByTourHash) {
  console.debug('downloadAndCachePerlschnurData: ' + timetable.length + ' items');
  console.time('downloadAndCachePerlschnurData');
  let downloadableUrlMap = {}; // map of url to value=true to indicate all items to download
  let completeUrlMap = {}; // map of all perlschnur url's

  await deleteDeprecatedPerlschnurCacheEntries(); // can be deleted when all users have been migrated

  let cache = await caches.open('fahrdienst452-perlschnur');
  let cacheKeys = await cache.keys();
  let cachedPerlschnurUrls = cacheKeys.map((k) => k.url) || [];

  let allPerlschnurUrls = timetable.map((i) => `${i.tourHash}/${i.checksum}`);
  let uniquePerlschnurUrls = [...new Set(allPerlschnurUrls)];

  console.debug('unique items: ' + uniquePerlschnurUrls.length);

  let duplicatePerlschnurCount = timetable.length - uniquePerlschnurUrls.length;
  let alreadyCachedPerlschnurCount = 0;
  // console.debug('cachedPerlschnurUrls', JSON.stringify(cachedPerlschnurUrls, null, 2));
  localStorage.removeItem('fahrdienst452/perlschnur'); // deprecated
  localStorage.removeItem('fahrdienst452/perlschnurJson'); // deprecated

  // find out which files need to be loaded and reduce duplicates
  uniquePerlschnurUrls.forEach((i, index) => {
    let tourHashChecksumKey = i;
    let url = fahrdienst452Api.getFetchPerlschnurUrl(tourHashChecksumKey);
    completeUrlMap[url] = tourHashChecksumKey;
    // add when not cached yet
    let findInCachedPerlschnurUrls = cachedPerlschnurUrls.find((longUrl) => longUrl.includes(url));
    if (!findInCachedPerlschnurUrls) {
      downloadableUrlMap[url] = true; // that is it has to be downloaded
    } else {
      alreadyCachedPerlschnurCount++;
    }
  });

  console.debug(
    uniquePerlschnurUrls.length + ' urls found, ' + duplicatePerlschnurCount + ' duplicates removed, ' + alreadyCachedPerlschnurCount + ' already cached'
  );

  // Delete perlschnur data from cache when not used anymore
  let completeUrlList = Object.keys(completeUrlMap);
  for (let i = 0; i < cachedPerlschnurUrls.length; i++) {
    let fqUrl = cachedPerlschnurUrls[i];
    let afterPerlschnur = fqUrl.substring(fqUrl.indexOf('perlschnur') + 8);
    let finding = completeUrlList.find((url) => url.includes(afterPerlschnur));
    if (!finding) {
      await cache.delete(fqUrl);
      console.debug('delete perlschnur data from cache. url', fqUrl);
    }
  }

  // Convert to iterable list
  let downloadableUrlList = Object.keys(downloadableUrlMap);

  // Iterate and fetch in batches to cache those resources
  let batchSize = 20;
  let promises = [];
  let batchTourHashChecksumKey = [];
  console.debug('downloading ' + downloadableUrlList.length + ' Perlschnur items...');
  for (let i = 0; i < downloadableUrlList.length; i++) {
    let url = downloadableUrlList[i];
    promises.push(fahrdienst452Api.fetchUrl(url));
    batchTourHashChecksumKey.push(completeUrlMap[url]);
    if (promises.length === batchSize || i === downloadableUrlList.length - 1) {
      //console.debug('perform perlschnur json data batch', i);
      let promiseResults = await Promise.all(promises);
      promiseResults.forEach((r, index) => {
        let tourHashChecksumKey = batchTourHashChecksumKey[index];
        perlschnurJsonByTourHash[tourHashChecksumKey] = r.data;
      });
      promises = [];
      batchTourHashChecksumKey = [];
    }
  }
  console.timeEnd('downloadAndCachePerlschnurData');
}
